import { useState, useEffect } from 'react';
import { Upload, Loader2, CheckCircle2, ArrowLeft, FileUp, Users } from 'lucide-react';
import { useSharing } from '@/hooks/use-sharing';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { UploadZone } from './UploadZone';
import { TransactionPreviewTable } from './TransactionPreviewTable';
import { ImportSummary } from './ImportSummary';
import { useImportStatement } from '@/hooks/use-import-statement';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/hooks/use-subscription';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';

interface Account {
  id: string;
  name: string;
  type: string;
  institution: string | null;
  user_id?: string;
  isShared?: boolean;
  ownerName?: string;
}

interface ImportStatementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaultAccountId?: string | null;
}

export function ImportStatementModal({ open, onOpenChange, onSuccess, defaultAccountId }: ImportStatementModalProps) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { activeAccess, currentUserId } = useSharing();
  const { canAccess } = useSubscription();

  const {
    step,
    isProcessing,
    result,
    transactions,
    importStats,
    processFile,
    toggleTransaction,
    selectAll,
    deselectAll,
    deselectDuplicates,
    updateTransactionCategory,
    confirmImport,
    reset
  } = useImportStatement();

  useEffect(() => {
    if (open) {
      fetchAccounts();
    }
  }, [open]);

  // Pre-select account when defaultAccountId changes
  useEffect(() => {
    if (open && defaultAccountId) {
      setSelectedAccountId(defaultAccountId);
    }
  }, [open, defaultAccountId]);

  const fetchAccounts = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      // Fetch all accessible accounts (own + shared with full permission via RLS)
      const { data } = await supabase
        .from('accounts')
        .select('id, name, type, institution, user_id')
        .eq('is_active', true)
        .eq('archived', false);

      // Mark shared accounts and add owner info
      const accountsWithSharing = (data || []).map(acc => {
        const isShared = acc.user_id !== user.id;
        const shareInfo = isShared
          ? activeAccess.find(s => s.owner_id === acc.user_id)
          : null;

        // Only include shared accounts with 'full' permission
        if (isShared && shareInfo?.permission_level !== 'full') {
          return null;
        }

        return {
          ...acc,
          isShared,
          ownerName: shareInfo?.owner?.full_name || undefined,
        };
      }).filter(Boolean) as Account[];

      setAccounts(accountsWithSharing);
      if (accountsWithSharing.length > 0 && !defaultAccountId) {
        setSelectedAccountId(accountsWithSharing[0].id);
      }
    }
  };

  const handleClose = () => {
    reset();
    setSelectedFile(null);
    onOpenChange(false);
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleStartProcessing = () => {
    if (selectedFile && selectedAccountId) {
      processFile(selectedFile, selectedAccountId);
    }
  };

  const handleConfirmImport = () => {
    confirmImport(selectedAccountId);
  };

  const handleComplete = () => {
    handleClose();
    onSuccess?.();
  };

  const selectedCount = transactions.filter(t => t.selected).length;
  const importLocked = !canAccess('statement_import');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn(
        "transition-all duration-300",
        step === 'preview' && !importLocked ? "max-w-4xl" : "max-w-lg"
      )}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {!importLocked && step === 'preview' && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 mr-2"
                onClick={reset}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {importLocked ? (
              <>
                <FileUp className="h-5 w-5 text-primary" />
                Importar Extrato
              </>
            ) : (
              <>
                {step === 'upload' && (
                  <>
                    <FileUp className="h-5 w-5 text-primary" />
                    Importar Extrato
                  </>
                )}
                {step === 'processing' && (
                  <>
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                    Processando...
                  </>
                )}
                {step === 'preview' && (
                  <>
                    <Upload className="h-5 w-5 text-primary" />
                    Revisar Transações
                  </>
                )}
                {step === 'complete' && (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-success" />
                    Importação Concluída
                  </>
                )}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {importLocked ? (
          <div className="py-4">
            <UpgradePrompt feature="statement_import" />
          </div>
        ) : (
          <>
            {/* Upload Step */}
            {step === 'upload' && (
              <div className="space-y-6 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Conta de Destino</label>
                  <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a conta" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map(account => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex items-center gap-2">
                            {account.isShared && (
                              <Users className="h-3 w-3 text-blue-500" />
                            )}
                            <span>
                              {account.name}
                              {account.institution && ` - ${account.institution}`}
                            </span>
                            {account.isShared && account.ownerName && (
                              <span className="text-xs text-muted-foreground">
                                ({account.ownerName})
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedAccountId && accounts.find(a => a.id === selectedAccountId)?.isShared && (
                    <p className="text-xs text-blue-600 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      Importando para conta compartilhada de {accounts.find(a => a.id === selectedAccountId)?.ownerName}
                    </p>
                  )}
                </div>

                <UploadZone onFileSelect={handleFileSelect} />

                <Button
                  className="w-full"
                  disabled={!selectedFile || !selectedAccountId}
                  onClick={handleStartProcessing}
                >
                  Processar Extrato
                </Button>
              </div>
            )}

            {/* Processing Step */}
            {step === 'processing' && (
              <div className="py-12 flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-medium">Analisando extrato...</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Extraindo transações e categorizando automaticamente
                  </p>
                </div>
              </div>
            )}

            {/* Preview Step */}
            {step === 'preview' && result && (
              <div className="space-y-6 py-4">
                <ImportSummary
                  summary={result.summary}
                  fileName={result.file_name}
                  bankName={result.bank_name}
                />

                {result.already_imported && (
                  <div className="p-3 rounded-lg bg-warning/10 border border-warning/20 text-sm text-warning">
                    ⚠️ Este arquivo já foi importado anteriormente em{' '}
                    {new Date(result.previous_import_date!).toLocaleDateString('pt-BR')}
                  </div>
                )}

                <TransactionPreviewTable
                  transactions={transactions}
                  onToggle={toggleTransaction}
                  onCategoryChange={updateTransactionCategory}
                  onSelectAll={selectAll}
                  onDeselectAll={deselectAll}
                  onDeselectDuplicates={deselectDuplicates}
                />

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={reset} className="flex-1">
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleConfirmImport}
                    disabled={selectedCount === 0 || isProcessing}
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      `Importar ${selectedCount} Transações`
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Complete Step */}
            {step === 'complete' && (
              <div className="py-8 flex flex-col items-center gap-6">
                <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center animate-bounce-once">
                  <CheckCircle2 className="w-12 h-12 text-success" />
                </div>

                <div className="text-center space-y-2">
                  <h3 className="text-xl font-bold">
                    {importStats.imported} transações importadas!
                  </h3>
                  <p className="text-muted-foreground">
                    {importStats.skipped > 0 && `${importStats.skipped} ignoradas • `}
                    {importStats.mappingsCreated > 0 && `${importStats.mappingsCreated} novos mapeamentos criados`}
                  </p>
                </div>

                <div className="flex gap-3 w-full">
                  <Button variant="outline" onClick={() => { reset(); }} className="flex-1">
                    Importar Outro
                  </Button>
                  <Button onClick={handleComplete} className="flex-1">
                    Ver Transações
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
